import React, { useCallback, useEffect, useState } from 'react'
import { useForm, FormProvider } from 'react-hook-form'
import { SvgIcon, useMediaQuery } from '@mui/material'
import PropTypes from 'prop-types'
import { useTranslation } from 'react-i18next'

import SearchIcon from '@hapana/shared/web/styles/assets/svgs/search.svg'
import ClearSearchIcon from '@hapana/shared/web/styles/assets/svgs/times-circle.svg'
import TextField from '@hapana/shared/web/components/shared/text-field/text-field.component'
import Button from '@hapana/shared/web/components/shared/button/button.component'
import { BUTTON_VARIANTS } from '@hapana/shared/web/components/shared/button/button.constants'
import { useDebounce } from '@hapana/shared/web/hooks/useDebounce'

import { SEARCH_DEBOUNCE } from './search.constants'
import { styles } from './search.styles'

const Search = ({ searchString, searchCallback, uid }) => {
  const { t } = useTranslation(['common', 'checkIn'])
  const matchesDesktop = useMediaQuery((theme) => theme.breakpoints.up('sm'))
  const formMethods = useForm({
    defaultValues: { search: searchString },
    mode: 'all',
  })
  const [isFocused, setIsFocused] = useState(false)
  const { watch, setValue } = formMethods
  const searchValue = watch('search')
  const hasClearButton = searchValue?.length > 0
  const debouncedSearchValue = useDebounce(searchValue, SEARCH_DEBOUNCE)
  const handleClearSearchField = useCallback(() => {
    setValue('search', '')
  }, [setValue])

  useEffect(() => {
    if (!searchString) {
      handleClearSearchField()
    }
  }, [searchString, handleClearSearchField])

  useEffect(() => {
    searchCallback(searchValue ? debouncedSearchValue : searchValue)
  }, [debouncedSearchValue, searchValue, searchCallback])

  return (
    <FormProvider {...formMethods}>
      <form>
        <TextField
          sx={styles.search}
          name="search"
          onKeyPress={(e) => {
            e.key === 'Enter' && e.preventDefault()
          }}
          placeholder={t(
            matchesDesktop ? 'checkIn:searchPlaceholder' : 'checkIn:searchPlaceholderMobile'
          )}
          InputProps={{
            onFocus: () => setIsFocused(true),
            startAdornment: <SvgIcon component={SearchIcon} sx={styles.searchIcon} />,
            endAdornment: (
              <>
                {hasClearButton && isFocused && (
                  <Button
                    variant={BUTTON_VARIANTS.ICON}
                    icon={ClearSearchIcon}
                    onClick={handleClearSearchField}
                    sx={styles.clearSearchIcon}
                  />
                )}
              </>
            ),
          }}
          uid={uid ? `${uid}-search` : ''}
        />
      </form>
    </FormProvider>
  )
}

Search.propTypes = {
  searchString: PropTypes.string,
  searchCallback: PropTypes.func,
  uid: PropTypes.string,
}

Search.defaultProps = {
  searchString: '',
  searchCallback: () => {},
}

export default React.memo(Search)
